// src/pages/CourseTime/components/ActivityModal.comp.jsx
import React, { useEffect } from 'react';
import {
  ActivityModalOverlay,
  ActivityModalContainer,
  ActivityModalHeader,
  ActivityModalTitle,
  ActivityCloseButton,
  ActivityTabsContainer,
  ActivityTab,
  ActivityContentArea,
  ActivityTabContent,
  ActivityResponsesContainer,
  ActivityLoadingContainer,
  ActivityLoadingSpinner,
  ActivityEmptyState,
  ActivityEmptyStateIcon,
  ActivityEmptyStateText,
  ActivityEmptyStateSubtext,
  ActivityErrorContainer,
  ActivityErrorText
} from '../styles/activity.styles';
import ActivityResponseDisplay from './ActivityResponseDisplay.comp.jsx';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

// Close icon SVG component
const CloseIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

// Empty state icon SVG component
const EmptyIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
);

const ActivityModal = ({
  isOpen,
  onClose,
  courseTitle,
  userActivities,
  activeTabIndex,
  onTabChange,
  getCurrentActivity,
  getCurrentActivityResponses,
  isCurrentActivityLoading,
  formatActivityType,
  formatTimestamp,
  loading,
  error,
  themeColor = '#e91e63', // Default fallback color
  pageLc
}) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click to close modal
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Render loading state
  if (loading) {
    return (
      <ActivityModalOverlay onClick={handleBackdropClick}>
        <ActivityModalContainer>
          <ActivityModalHeader themeColor={themeColor}>
            <ActivityModalTitle themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__activity_responses')}</ActivityModalTitle>
            <ActivityCloseButton themeColor={themeColor} onClick={onClose}>
              <CloseIcon />
            </ActivityCloseButton>
          </ActivityModalHeader>
          <ActivityLoadingContainer>
            <ActivityLoadingSpinner themeColor={themeColor} />
            {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_activities')}...
          </ActivityLoadingContainer>
        </ActivityModalContainer>
      </ActivityModalOverlay>
    );
  }

  // Render error state
  if (error) {
    return (
      <ActivityModalOverlay onClick={handleBackdropClick}>
        <ActivityModalContainer>
          <ActivityModalHeader themeColor={themeColor}>
            <ActivityModalTitle themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__activity_responses')}</ActivityModalTitle>
            <ActivityCloseButton themeColor={themeColor} onClick={onClose}>
              <CloseIcon />
            </ActivityCloseButton>
          </ActivityModalHeader>
          <ActivityErrorContainer>
            <ActivityErrorText>{error}</ActivityErrorText>
          </ActivityErrorContainer>
        </ActivityModalContainer>
      </ActivityModalOverlay>
    );
  }

  // Render empty state when no activities
  if (!userActivities || userActivities.length === 0) {
    return (
      <ActivityModalOverlay onClick={handleBackdropClick}>
        <ActivityModalContainer>
          <ActivityModalHeader themeColor={themeColor}>
            <ActivityModalTitle themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__activity_responses')}</ActivityModalTitle>
            <ActivityCloseButton themeColor={themeColor} onClick={onClose}>
              <CloseIcon />
            </ActivityCloseButton>
          </ActivityModalHeader>
          <ActivityEmptyState>
            <ActivityEmptyStateIcon themeColor={themeColor}>
              <EmptyIcon />
            </ActivityEmptyStateIcon>
            <ActivityEmptyStateText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_activities_found')}</ActivityEmptyStateText>
            <ActivityEmptyStateSubtext>
              {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_activities_found_subtext')}
            </ActivityEmptyStateSubtext>
          </ActivityEmptyState>
        </ActivityModalContainer>
      </ActivityModalOverlay>
    );
  }

  const currentActivity = getCurrentActivity();
  const currentResponses = getCurrentActivityResponses();
  const isResponsesLoading = isCurrentActivityLoading();

  return (
    <ActivityModalOverlay onClick={handleBackdropClick}>
      <ActivityModalContainer>
        <ActivityModalHeader themeColor={themeColor}>
          <ActivityModalTitle themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__activity_responses')} - {courseTitle}</ActivityModalTitle>
          <ActivityCloseButton themeColor={themeColor} onClick={onClose}>
            <CloseIcon />
          </ActivityCloseButton>
        </ActivityModalHeader>

        {/* Activity Tabs */}
        <ActivityTabsContainer themeColor={themeColor}>
          {userActivities.map((activity, index) => (
            <ActivityTab
              key={activity.slide_id}
              active={index === activeTabIndex}
              onClick={() => onTabChange(index)}
              themeColor={themeColor}
            >
              {formatActivityType(activity.activity_type)} {index + 1}
            </ActivityTab>
          ))}
        </ActivityTabsContainer>

        {/* Content Area */}
        <ActivityContentArea>
          <ActivityTabContent>
            {currentActivity && (
              <>
                {/* Activity Title */}
                <div style={{ 
                  padding: '16px 24px', 
                  borderBottom: '1px solid #f1f5f9',
                  backgroundColor: '#fafafa'
                }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: themeColor 
                  }}>
                    {currentActivity.slide_title}
                  </h3>
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '13px', 
                    color: '#6b7280' 
                  }}>
                    {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__session')} {currentActivity.session_no} • {formatActivityType(currentActivity.activity_type)}
                  </p>
                </div>

                {/* Responses Container */}
                <ActivityResponsesContainer themeColor={themeColor}>
                  {isResponsesLoading ? (
                    <ActivityLoadingContainer>
                      <ActivityLoadingSpinner themeColor={themeColor} />
                      {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_activity_responses')}...
                    </ActivityLoadingContainer>
                  ) : currentResponses ? (
                    // For poll activities, show chart even if no responses
                    // For text activities, only show if there are responses
                    (currentActivity.activity_type === 'POLL_QUIZ_ACTIVITY' && currentResponses.poll_results) ||
                    (currentActivity.activity_type !== 'POLL_QUIZ_ACTIVITY' && currentResponses.responses && currentResponses.responses.length > 0) ? (
                      <ActivityResponseDisplay
                        activityType={currentActivity.activity_type}
                        responses={currentResponses.responses}
                        pollResults={currentResponses.poll_results}
                        formatTimestamp={formatTimestamp}
                        themeColor={themeColor}
                        currentActivity={currentActivity}
                        pageLc={pageLc}
                      />
                    ) : (
                      <ActivityEmptyState>
                        <ActivityEmptyStateIcon themeColor={themeColor}>
                          <EmptyIcon />
                        </ActivityEmptyStateIcon>
                        <ActivityEmptyStateText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_responses_yet')}</ActivityEmptyStateText>
                        <ActivityEmptyStateSubtext>
                          {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_responses_yet_subtext')}
                        </ActivityEmptyStateSubtext>
                      </ActivityEmptyState>
                    )
                  ) : (
                    <ActivityEmptyState>
                      <ActivityEmptyStateIcon themeColor={themeColor}>
                        <EmptyIcon />
                      </ActivityEmptyStateIcon>
                      <ActivityEmptyStateText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_data_available')}</ActivityEmptyStateText>
                      <ActivityEmptyStateSubtext>
                        {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__unable_load_responses')}
                      </ActivityEmptyStateSubtext>
                    </ActivityEmptyState>
                  )}
                </ActivityResponsesContainer>
              </>
            )}
          </ActivityTabContent>
        </ActivityContentArea>
      </ActivityModalContainer>
    </ActivityModalOverlay>
  );
};

export default ActivityModal;
